import {typeName} from './events'
import {formatDate} from './gramps'
import {fullName, placePath, shortName} from './tree'
import type {TreeIndex} from './types'

/* Что именно поменялось в правке.
 *
 * Журнал хранит объект целиком до и после — Gramps кладёт в undo.db оба
 * снимка, API отдаёт их по запросу (?old=true&new=true). Мы сравниваем
 * поле за полем и переводим на человеческий: «Пол: женский → мужской»,
 * «Заметки: добавлена одна».
 *
 * Сравнивать всё подряд смысла нет: в каждой правке меняется служебное
 * поле change (время правки) и иногда порядок ссылок, который на смысл
 * не влияет. Поэтому список полей — явный, по каждому классу.
 */

export interface FieldDiff {
  label: string
  /** пусто — значение не было заполнено */
  was: string
  now: string
}

type Raw = Record<string, unknown>

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v))
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])

const GENDER = ['женский', 'мужской', 'неизвестен']

/** Имя из primary_name: у Gramps фамилия лежит отдельным списком. */
function nameOf(v: unknown): string {
  const n = v as {first_name?: string; surname_list?: {surname?: string}[]; suffix?: string} | undefined
  if (!n) return ''
  const sur = n.surname_list?.[0]?.surname || ''
  return [sur, n.first_name || ''].filter(Boolean).join(' ')
}

function dateOf(v: unknown): string {
  const d = v as {dateval?: number[]; modifier?: number} | undefined
  if (!d?.dateval) return ''
  const s = formatDate(d.dateval)
  // диапазон: у span вторая дата лежит хвостом того же массива
  if (d.modifier === 5 && d.dateval.length >= 8) {
    const to = formatDate(d.dateval.slice(4) as number[])
    return [s, to].filter(Boolean).join(' — ')
  }
  return s
}

/* У каждого класса свой перечень: значение 5 у события — это не то же
   самое, что 5 у места. Поэтому таблица передаётся отдельно. */
const FAMILY_REL = ['брак', 'без регистрации', 'гражданский союз', 'неизвестно', 'иное']

const PLACE_TYPE = [
  'неизвестно', 'иное', 'страна', 'штат', 'уезд', 'город', 'приход', 'местность',
  'улица', 'провинция', 'область', 'департамент', 'район', 'округ', 'боро',
  'муниципалитет', 'городок', 'село', 'хутор', 'ферма', 'здание', 'номер',
]

const typeOf = (v: unknown, table?: string[]): string => {
  const t = v as {value?: number; string?: string} | undefined
  if (t?.string) return t.string
  if (t?.value === undefined || t.value < 0) return ''
  if (table) return table[t.value] || `тип ${t.value}`
  return typeName(t.value)
}

/** Ссылки считаем, а не перечисляем: «3 → 4» ничего не говорит, а
 *  «добавлено 1» — говорит. */
function countChange(was: unknown[], now: unknown[], one: string, many: string) {
  const d = now.length - was.length
  if (d > 0) return {was: '', now: `добавлено ${d === 1 ? one : `${d} ${many}`}`}
  if (d < 0) return {was: `удалено ${-d === 1 ? one : `${-d} ${many}`}`, now: ''}
  return {was: 'переставлены', now: ''}
}

const refs = (v: unknown): string[] =>
  list(v).map(x => (typeof x === 'string' ? x : str((x as {ref?: string})?.ref)))

/* Создание и удаление сравниваются с пустотой: у созданной записи нет
   снимка «до», у удалённой — «после». Пустой объект вместо null даёт ровно
   то, что нужно видеть — весь состав новой записи или всё, что потеряно
   при удалении. */
export function diffFields(
  cls: string, oldRaw: Raw | null, newRaw: Raw | null, ix: TreeIndex,
): FieldDiff[] {
  if (!oldRaw && !newRaw) return []
  const old = oldRaw || {}
  const now = newRaw || {}
  const out: FieldDiff[] = []
  const add = (label: string, a: string, b: string) => {
    if (a !== b) out.push({label, was: a, now: b})
  }
  /* Сравниваем через list(): у созданной записи поля просто нет, а пустой
     список и отсутствие поля — одно и то же, и «изменено» тут ложь. */
  const same = (k: string) => JSON.stringify(list(old[k])) === JSON.stringify(list(now[k]))
  const counted = (k: string, label: string, one: string, many: string) => {
    if (same(k)) return
    const c = countChange(list(old[k]), list(now[k]), one, many)
    out.push({label, ...c})
  }
  const place = (h: unknown) => (h ? placePath(ix, str(h)) || 'место' : '')
  const person = (h: unknown) => (h ? fullName(ix.people.get(str(h))) || 'человек' : '')

  if (cls === 'Person' || cls === '0') {
    add('Имя', nameOf(old.primary_name), nameOf(now.primary_name))
    add('Пол', GENDER[Number(old.gender)] ?? '', GENDER[Number(now.gender)] ?? '')
    add('Скрытая запись', old.private ? 'да' : 'нет', now.private ? 'да' : 'нет')
    counted('event_ref_list', 'События', 'событие', 'события')
    counted('note_list', 'Заметки', 'заметка', 'заметки')
    counted('media_list', 'Снимки', 'снимок', 'снимка')
    counted('alternate_names', 'Другие имена', 'имя', 'имени')
    if (!same('parent_family_list')) out.push({label: 'Родители', was: '', now: 'связь изменена'})
    if (!same('family_list')) out.push({label: 'Семья', was: '', now: 'связь изменена'})
    // главное фото — первый снимок в списке, его подмену видно отдельно
    const p0 = (v: unknown) => refs(v)[0] || ''
    if (p0(old.media_list) !== p0(now.media_list) && refs(old.media_list).length === refs(now.media_list).length)
      out.push({label: 'Главное фото', was: '', now: 'заменено'})
  } else if (cls === 'Event' || cls === '3') {
    add('Тип', typeOf(old.type), typeOf(now.type))
    add('Дата', dateOf(old.date), dateOf(now.date))
    add('Место', place(old.place), place(now.place))
    add('Описание', str(old.description), str(now.description))
  } else if (cls === 'Family' || cls === '1') {
    add('Отец', person(old.father_handle), person(now.father_handle))
    add('Мать', person(old.mother_handle), person(now.mother_handle))
    add('Отношения', typeOf(old.type, FAMILY_REL), typeOf(now.type, FAMILY_REL))
    counted('child_ref_list', 'Дети', 'ребёнок', 'детей')
    counted('event_ref_list', 'События', 'событие', 'события')
  } else if (cls === 'Place' || cls === '5') {
    add('Название', str((old.name as {value?: string})?.value), str((now.name as {value?: string})?.value))
    add('Тип', typeOf(old.place_type, PLACE_TYPE), typeOf(now.place_type, PLACE_TYPE))
    add('Широта', str(old.lat), str(now.lat))
    add('Долгота', str(old.long), str(now.long))
    if (!same('placeref_list')) out.push({label: 'Входит в', was: '', now: 'изменено'})
  } else if (cls === 'Media' || cls === '4') {
    add('Подпись', str(old.desc), str(now.desc))
    add('Файл', str(old.path), str(now.path))
    add('Дата', dateOf(old.date), dateOf(now.date))
    counted('tag_list', 'Метки', 'метка', 'метки')
  } else if (cls === 'Note' || cls === '8') {
    const text = (v: unknown) => str((v as {string?: string})?.string).trim()
    add('Текст', short(text(old.text)), short(text(now.text)))
  }

  return out
}

/** Заметки бывают длинными, а в журнале нужна узнаваемость, не полный текст. */
const short = (s: string, n = 90) => (s.length > n ? `${s.slice(0, n)}…` : s)

/** Что за объект — коротко, для заголовка развёрнутой правки. */
export function objTitle(cls: string, data: Raw | null, ix: TreeIndex): string {
  if (!data) return ''
  if (cls === 'Person' || cls === '0') return nameOf(data.primary_name)
  if (cls === 'Event' || cls === '3')
    return [typeOf(data.type), dateOf(data.date)].filter(Boolean).join(', ')
  if (cls === 'Place' || cls === '5') return str((data.name as {value?: string})?.value)
  if (cls === 'Media' || cls === '4') return str(data.desc)
  if (cls === 'Family' || cls === '1')
    return [data.father_handle, data.mother_handle]
      .filter(Boolean)
      .map(h => shortName(ix.people.get(str(h))))
      .join(' + ')
  return ''
}
